
import { supabase } from './supabaseClient';
import { WorkerProfile, JobRequest, UserProfile, ServiceCategory, PlatformFeeConfig, JobStatus, Report, WithdrawalRequest, Review, AdBanner, Transaction, UserRole, ChatMessage } from '../types';
import { DEFAULT_FEE_CONFIG, DEFAULT_SERVICE_CATEGORIES } from '../constants';

/**
 * Service Layer (Supabase Implementation)
 */

export const getUserProfile = async (identifier: string): Promise<UserProfile | null> => {
    if (!identifier) return null;

    try {
        const isEmail = identifier.includes('@');
        // Simple regex for UUID (8-4-4-4-12 hex digits)
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);

        let col = 'phone';
        if (isEmail) col = 'email';
        else if (isUUID) col = 'id';

        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq(col, identifier)
            .maybeSingle(); // Use maybeSingle to handle no results gracefully

        if (error) {
            // 406 errors typically mean RLS policy rejection or no matching row
            if (error.code === 'PGRST116' || error.message.includes('406')) {
                console.warn('Profile not found or access denied:', identifier);
                return null;
            }
            console.error('Error fetching profile:', error);
            return null; // Return null to allow onboarding logic to trigger
        }

        if (!data) {
            console.warn('No profile found for identifier:', identifier);
            return null;
        }

        // Validate profile is active (not deleted or suspended for login purposes)
        if (data.status === 'deleted') {
            console.warn('Profile is marked as deleted:', identifier);
            return null;
        }

        return mapProfileFromDB(data);
    } catch (e) {
        console.error('Unexpected error in getUserProfile:', e);
        return null;
    }
};

// Helper: Map DB snake_case to TS camelCase
const mapProfileFromDB = (data: any): UserProfile => ({
    ...data,
    firstName: data.first_name,
    lastName: data.last_name,
    walletBalance: data.wallet_balance,
    avatarUrl: data.avatar_url,
    // Admin permissions logic
    adminPermissions: data.is_admin ? ['dashboard', 'users', 'workers', 'categories', 'withdrawals', 'ads', 'cms', 'reports', 'settings', 'firebase', 'messages', 'administrators'] : []
});

const mapWorkerFromDB = (data: any): WorkerProfile => ({
    ...data,
    hourlyRate: data.hourly_rate,
    isOnline: data.is_online,
    reviewCount: data.review_count,
    verificationStatus: data.verification_status,
    idProofUrl: data.id_proof_url,
    lifetimeJobsCompleted: data.lifetime_jobs_completed,
    totalEarned: data.total_earned,
    // ✅ FIXED: Properly map avatar from workers table
    avatarUrl: data.avatar_url || '/default-avatar.png',
    // ✅ FIXED: Map cover image
    coverImageUrl: data.cover_image_url || null,
    // ✅ FIXED: Properly map portfolio items
    portfolioItems: data.portfolio_items || [],
    // ✅ FIXED: Map tags
    tags: data.tags || [],
    // ✅ CRITICAL: Map phone for booking
    phone: data.phone || null,
    // ✅ FIXED: Map distance from database
    distanceKm: data.distance_km || 0,
    // Location is special, might modify schema to return lat/lng cols or parse
    coordinates: data.location ? { x: 0, y: 0 } : { x: 0, y: 0 } // handled via RPC mostly
});

export const listenToUserProfile = (userId: string, callback: (profile: UserProfile | null) => void) => {
    // Initial fetch
    getUserProfile(userId).then(callback);

    const filter = `id=eq.${userId}`;

    const channel = supabase
        .channel(`public:profiles:${filter}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles', filter: filter }, (payload) => {
            if (payload.new) {
                callback(mapProfileFromDB(payload.new));
            } else {
                callback(null);
            }
        })
        .subscribe();

    return () => { supabase.removeChannel(channel); };
};

export const saveUserProfile = async (profile: UserProfile): Promise<void> => {
    // If we have a profile ID, we use it (especially for admin updates)
    // Fallback to auth.getUser() only if id is missing
    let targetId = profile.id;

    if (!targetId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("No authenticated user found");
        targetId = user.id;
    }

    const dbProfile = {
        id: targetId,
        phone: (profile.phone && profile.phone.trim().length > 0) ? profile.phone.trim() : null,
        first_name: profile.firstName,
        last_name: profile.lastName,
        email: profile.email || null,
        gender: profile.gender,
        dob: profile.dob,
        avatar_url: profile.avatarUrl,
        language: profile.language || 'en',
        status: profile.status || 'active',
        role: profile.role || UserRole.CUSTOMER,
        is_admin: profile.role === UserRole.ADMIN
    };

    const { error } = await supabase.from('profiles').upsert(dbProfile, { onConflict: 'id' });
    if (error) throw error;

    // Invalidate cache on profile update (names/avatars might change in worker lists)
    invalidateWorkersCache();

    // SYNC: Handled by DB Trigger 'sync_profile_to_worker'
    // const workerUpdate = {
    //     name: `${profile.firstName} ${profile.lastName}`,
    //     avatar_url: profile.avatarUrl,
    //     phone: profile.phone
    // };

    // 1. Update existing worker record if it exists
    // const { error: workerError } = await supabase.from('workers').update(workerUpdate).eq('id', targetId);

    // 2. If user is a WORKER but no worker record exists, initialize it
    if (profile.role === UserRole.WORKER) {
        const { data: existingWorker } = await supabase.from('workers').select('id').eq('id', targetId).maybeSingle();
        if (!existingWorker) {
            console.log("Initializing new worker profile for:", targetId);
            await supabase.from('workers').insert({
                id: targetId,
                phone: profile.phone,
                name: `${profile.firstName} ${profile.lastName}`,
                skill: 'General', // Default skill for promoted users
                status: 'active',
                verification_status: 'verified' // Auto-approved when admin promotes
            });
        }
    }



    // SYNC: Update active jobs with new customer details (phone, name)
    try {
        const fullName = `${profile.firstName} ${profile.lastName}`;
        const jobsUpdate = {
            customer_name: fullName,
            customer_phone: profile.phone,
            customer_avatar: profile.avatarUrl
        };
        // Update all jobs for this customer that are active (OPEN, PENDING, IN_PROGRESS, ACCEPTED)
        // We avoid updating COMPLETED/CANCELLED to preserve history.
        const { error: jobError } = await supabase
            .from('jobs')
            .update(jobsUpdate)
            .eq('customer_id', targetId)
            .in('status', ['OPEN', 'PENDING', 'IN_PROGRESS', 'ACCEPTED', 'OTP_VERIFIED']);

        if (jobError) console.error("Failed to sync customer details to jobs:", jobError);
        else console.log("Synced customer details to active jobs.");
    } catch (e) {
        console.error("Error syncing jobs:", e);
    }
};

// ✅ EGRESS OPTIMIZATION: In-memory cache for workers
let workersCache: { data: WorkerProfile[]; timestamp: number } | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const invalidateWorkersCache = () => {
    console.log("♻️ Invalidating Workers Cache");
    workersCache = null;
};

// Consolidated getAllWorkers function with caching
export const getAllWorkers = async (forceRefresh = false, includeAll = false): Promise<WorkerProfile[]> => {
    // Check cache first (unless force refresh)
    if (!forceRefresh && workersCache && Date.now() - workersCache.timestamp < CACHE_DURATION) {
        console.log("✅ Using cached workers data");
        return workersCache.data;
    }

    console.log("🔄 Fetching fresh workers data from database");

    // 1. Fetch Workers - Filter by status unless admin needs all
    // ✅ EGRESS OPTIMIZATION: Select only needed columns instead of *
    // NOTE: 'status' is omitted from first select as it may not exist in the workers table
    let query = supabase.from('workers').select(`
        id, phone, name, skill, rating, review_count,
        hourly_rate, is_online, avatar_url, cover_image_url, bio,
        verification_status, location, lifetime_jobs_completed,
        total_earned, id_proof_url, tags, portfolio_items,
        upi_id, subscription_plan, subscription_end
    `);

    // Only filter by status if we are NOT in admin mode and we are certain about the column
    // For now, we rely on profileMap for filtering to be safe
    const { data: workersData, error } = await query;

    if (error) {
        console.error("Error fetching workers:", error);
        return workersCache?.data || []; // Return cached data on error
    }

    // 2. Fetch Profiles for Email/Avatar mapping and Ghost Expert detection
    // ✅ EGRESS OPTIMIZATION: Select only needed columns
    // We include 'role' and 'first_name', 'last_name' to detect experts missing from workers table
    const { data: profilesData } = await supabase.from('profiles').select('id, phone, email, status, avatar_url, role, first_name, last_name');
    const profileMap = new Map();
    (profilesData || []).forEach((p: any) => {
        const mapped = {
            email: p.email,
            status: p.status,
            avatarUrl: p.avatar_url,
            role: p.role,
            name: `${p.first_name || ''} ${p.last_name || ''}`.trim()
        };
        if (p.phone) profileMap.set(p.phone, mapped);
        if (p.id) profileMap.set(p.id, mapped);
    });

    const workers = (workersData || [])
        .map((w: any) => {
            const profileData = profileMap.get(w.phone) || profileMap.get(w.id);
            const status = profileData?.status || w.status || 'active';

            return {
                id: w.id,
                phone: w.phone,
                email: profileData?.email,
                name: w.name,
                skill: w.skill,
                rating: w.rating,
                reviewCount: w.review_count,
                distanceKm: w.distance_km || 0,
                hourlyRate: w.hourly_rate,
                isOnline: w.is_online,
                // ✅ FIXED: Prioritize avatar from workers table (worker's uploaded image)
                avatarUrl: w.avatar_url || profileData?.avatarUrl || '/default-avatar.png',
                // ✅ NEW: Cover image support
                coverImageUrl: w.cover_image_url || null,
                bio: w.bio,
                verificationStatus: w.verification_status,
                coordinates: w.coordinates ? (typeof w.coordinates === 'string' ? JSON.parse(w.coordinates) : w.coordinates) : { x: 0, y: 0 },
                lifetimeJobsCompleted: w.lifetime_jobs_completed,
                totalEarned: w.total_earned,
                status: status,
                idProofUrl: w.id_proof_url,
                tags: w.tags || [],
                portfolioItems: w.portfolio_items || [],
                upiId: w.upi_id || null, // ✅ Payment
                subscriptionPlan: w.subscription_plan || 'trial',
                subscriptionEnd: w.subscription_end
            };
        })
        .filter((w: any) => {
            // Apply filtering here instead of database query for maximum compatibility
            return includeAll || (w.status === 'active');
        });

    // 🚀 NEW: Detect "Ghost Experts" (role=worker in profiles but missing in workers table)
    // We check against ALL IDs in the workers table to avoid duplicate entries for suspended workers
    const allTableWorkerIds = new Set((workersData || []).map(w => w.id));
    const ghostExperts = (profilesData || [])
        .filter(p => {
            const isWorker = p.role?.toUpperCase() === UserRole.WORKER;
            const notInTable = !allTableWorkerIds.has(p.id);
            const isActive = includeAll || p.status === 'active';
            return isWorker && notInTable && isActive;
        })
        .map(p => ({
            id: p.id,
            phone: p.phone,
            email: p.email,
            name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Incomplete Expert',
            skill: 'General (Auto-Synced)',
            rating: 5.0,
            reviewCount: 0,
            distanceKm: 0,
            hourlyRate: 300,
            isOnline: false,
            avatarUrl: p.avatar_url || '/default-avatar.png',
            status: p.status || 'active',
            verificationStatus: 'verified' as const, // Auto-approve ghosts if they are verified profiles
            tags: [],
            portfolioItems: [],
            subscriptionPlan: 'trial',
            bio: 'Synthesized Expert Profile',
            coordinates: { x: 0, y: 0 },
            lifetimeJobsCompleted: 0,
            totalEarned: 0
        }));

    const finalWorkers = [...workers, ...ghostExperts];

    // Update cache
    workersCache = { data: finalWorkers, timestamp: Date.now() };
    console.log(`✅ Cached ${finalWorkers.length} workers (including ${ghostExperts.length} ghosts)`);

    return finalWorkers;
};

// NEW: Fetch Single Worker Profile (Directly by ID or Phone)
export const getWorkerProfile = async (identifier: string): Promise<WorkerProfile | null> => {
    try {
        // 1. Fetch Worker Data (no status filter to allow pending workers to load their own profile)
        let query = supabase.from('workers').select('*');

        // Determine if identifier is ID or Phone
        // UUID regex
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);

        if (isUUID) {
            query = query.eq('id', identifier);
        } else {
            query = query.eq('phone', identifier);
        }

        // Network Retry Logic (Fix for ERR_QUIC_PROTOCOL_ERROR)
        let workerData = null;
        let error = null;

        for (let i = 0; i < 3; i++) {
            try {
                const res = await query.maybeSingle();
                if (res.error) throw res.error;
                workerData = res.data;
                error = null;
                break; // Success
            } catch (e: any) {
                error = e;
                console.warn(`Worker fetch attempt ${i + 1} failed:`, e.message);
                if (i < 2) await new Promise(r => setTimeout(r, 500)); // Wait 500ms
            }
        }

        if (error || !workerData) {
            console.warn("Worker not found:", identifier);
            return null;
        }

        // 2. Fetch Profile for Email/Status/Avatar sync
        const { data: profileData } = await supabase
            .from('profiles')
            .select('email, status, avatar_url')
            .eq(isUUID ? 'id' : 'phone', identifier)
            .maybeSingle();

        // ✅ DEBUG: Log fetched data
        console.log(`📊 Fetched worker profile for ${workerData.name}:`, {
            worker_avatar: workerData.avatar_url,
            profile_avatar: profileData?.avatar_url,
            portfolio_count: workerData.portfolio_items?.length || 0,
            cover_image: workerData.cover_image_url
        });

        return {
            id: workerData.id,
            phone: workerData.phone,
            email: profileData?.email,
            name: workerData.name,
            skill: workerData.skill,
            rating: workerData.rating,
            reviewCount: workerData.review_count,
            distanceKm: workerData.distance_km || 0,
            hourlyRate: workerData.hourly_rate,
            isOnline: workerData.is_online,
            // ✅ FIXED: Prioritize avatar from workers table (worker's uploaded image)
            avatarUrl: workerData.avatar_url || profileData?.avatar_url || '/default-avatar.png',
            // ✅ NEW: Cover image support
            coverImageUrl: workerData.cover_image_url || null,
            bio: workerData.bio,
            verificationStatus: workerData.verification_status,
            coordinates: typeof workerData.coordinates === 'string' ? JSON.parse(workerData.coordinates) : workerData.coordinates,
            lifetimeJobsCompleted: workerData.lifetime_jobs_completed,
            totalEarned: workerData.total_earned,
            status: workerData.status,
            idProofUrl: workerData.id_proof_url,
            tags: workerData.tags || [],
            portfolioItems: workerData.portfolio_items || [],
            upiId: workerData.upi_id || null, // ✅ Payment
            subscriptionPlan: workerData.subscription_plan || 'trial',
            subscriptionEnd: workerData.subscription_end
        };
    } catch (e) {
        console.error("Error fetching single worker:", e);
        return null;
    }
};

export const saveWorkerProfile = async (worker: WorkerProfile): Promise<void> => {
    // Verify Auth Context
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) throw new Error("Security Error: You must be logged in to save profile.");

    if (worker.id !== user.id) {
        // Allow Admins to override this check
        const { data: adminCheck } = await supabase.from('profiles').select('is_admin').eq('id', user.id).single();
        if (!adminCheck?.is_admin) {
            console.error("⛔ Blocked attempt to edit another user's profile:", { current: user.id, target: worker.id });
            throw new Error("Security Error: ID Mismatch. You can only save your own profile.");
        } else {
            console.log("🛡️ Admin Override: Editing worker profile", worker.id);
        }
    }

    const dbWorker: any = {
        id: worker.id, // Verified against Auth ID
        phone: worker.phone,
        name: worker.name,
        skill: worker.skill,
        tags: worker.tags,
        hourly_rate: worker.hourlyRate,
        is_online: worker.isOnline,
        bio: worker.bio,
        verification_status: worker.verificationStatus,
        id_proof_url: worker.idProofUrl,
        portfolio_items: worker.portfolioItems || [],
        avatar_url: worker.avatarUrl, // ✅ Now saving avatar in workers table
        cover_image_url: worker.coverImageUrl || null, // ✅ New: Cover image support
        upi_id: worker.upiId || null, // ✅ Payment UPI ID
    };

    if (worker.coordinates) {
        // PostGIS format: POINT(long lat)
        dbWorker.location = `POINT(${worker.coordinates.y} ${worker.coordinates.x})`;
    }

    console.log("Saving Worker Data:", dbWorker);

    // Use ID as the conflict target
    const { error } = await supabase.from('workers').upsert(dbWorker, { onConflict: 'id' });
    if (error) {
        console.error("Error saving worker profile:", error);
        throw error;
    }

    // Invalidate cache immediately
    invalidateWorkersCache();

    // SYNC: Also update avatar in profiles table for consistency
    // This ensures avatar is synced across both tables
    // HANDLED BY DB TRIGGER 'sync_worker_to_profile'
    /*
    if (worker.avatarUrl) {
        const { error: profileError } = await supabase
            .from('profiles')
            .update({ avatar_url: worker.avatarUrl })
            .eq('id', worker.id);

        if (profileError) {
            console.warn("Failed to sync avatar to profiles table:", profileError);
            // Don't throw - worker profile is saved, this is just a sync issue
        } else {
            console.log("✅ Avatar synced to profiles table");
        }
    }
    */

    console.log("Worker Profile Saved Successfully!");

    // SYNC: Update active jobs with new worker details (phone, name, avatar)
    try {
        const jobsUpdate = {
            worker_name: worker.name,
            worker_phone: worker.phone,
            worker_avatar: worker.avatarUrl
        };
        // Update all jobs for this worker that are active (ACCEPTED, IN_PROGRESS, OTP_VERIFIED)
        // We avoid updating COMPLETED/CANCELLED to preserve history.
        const { error: jobError } = await supabase
            .from('jobs')
            .update(jobsUpdate)
            .eq('worker_id', worker.id)
            .in('status', ['ACCEPTED', 'IN_PROGRESS', 'OTP_VERIFIED']);

        if (jobError) console.error("Failed to sync worker details to jobs:", jobError);
        else console.log("Synced worker details to active jobs.");
    } catch (e) {
        console.error("Error syncing worker jobs:", e);
    }
};

export const listenToAllWorkers = (callback: (workers: WorkerProfile[]) => void, includeAll = false) => {
    let currentWorkers: WorkerProfile[] = [];

    // Initial fetch
    getAllWorkers(true, includeAll).then(workers => {
        currentWorkers = workers;
        callback(currentWorkers);
    });

    const channel = supabase
        .channel('public:workers:all')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'workers' }, (payload) => {
            console.log("Real-time Workers Change:", payload.eventType);
            if (payload.eventType === 'INSERT') {
                const newWorker = mapWorkerFromDB(payload.new);
                currentWorkers = [...currentWorkers, newWorker];
            } else if (payload.eventType === 'UPDATE') {
                currentWorkers = currentWorkers.map(w => w.id === payload.new.id ? { ...w, ...mapWorkerFromDB(payload.new) } : w);
            } else if (payload.eventType === 'DELETE') {
                // ✅ FIXED: Corrected delete filter logic (w.id !== payload.old.id)
                currentWorkers = currentWorkers.filter(w => w.id !== payload.old.id);
            }
            callback(currentWorkers);
        })
        .subscribe();

    return () => { supabase.removeChannel(channel); };
};

export const getAllUsers = async (): Promise<UserProfile[]> => {
    const { data } = await supabase.from('profiles').select('*');
    return (data || []).map(mapProfileFromDB);
};

// Consolidated getAllUsers with Real-time support
export const listenToAllProfiles = (callback: (profiles: UserProfile[]) => void) => {
    let currentProfiles: UserProfile[] = [];

    // Initial fetch
    getAllUsers().then(profiles => {
        currentProfiles = profiles;
        callback(currentProfiles);
    });

    const channel = supabase
        .channel('public:profiles:all')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, (payload) => {
            console.log("Real-time Profiles Change:", payload.eventType);
            if (payload.eventType === 'INSERT') {
                currentProfiles = [...currentProfiles, mapProfileFromDB(payload.new)];
            } else if (payload.eventType === 'UPDATE') {
                currentProfiles = currentProfiles.map(p => p.id === payload.new.id ? mapProfileFromDB(payload.new) : p);
            } else if (payload.eventType === 'DELETE') {
                currentProfiles = currentProfiles.filter(p => p.id === payload.old.id);
            }
            callback(currentProfiles);
        })
        .subscribe();

    return () => { supabase.removeChannel(channel); };
};

export const deleteUser = async (userId: string): Promise<void> => {
    try {
        // 1. Delete from profiles (CASCADE will handle workers/jobs)
        const { error: profileError } = await supabase.from('profiles').delete().eq('id', userId);

        if (profileError) {
            console.error("Delete User Profile Error:", profileError);
            throw profileError;
        }

        // 2. Auth user cleanup NOTE:
        // Deleting a Supabase auth user requires service_role key which MUST NOT
        // be exposed in client-side code. This should be handled by a Supabase
        // Edge Function or database trigger. Profile deletion above is sufficient
        // for blocking access (RLS relies on profile existence).
        console.log("Profile deleted for user:", userId);
        console.warn("⚠️ Auth user not deleted (requires server-side Edge Function). Manual cleanup may be needed.");
    } catch (e) {
        console.error("Critical error in deleteUser:", e);
        throw e;
    }
};

export const setUserStatus = async (userId: string, status: string): Promise<void> => {
    console.log("setUserStatus called:", { userId, status });
    const { data, error } = await supabase.from('profiles').update({ status }).eq('id', userId);
    console.log("setUserStatus result:", { data, error });
    if (error) {
        console.error("setUserStatus ERROR:", error);
        throw error;
    }
};

// ADMIN ONLY: Update worker verification status
export const updateWorkerVerification = async (workerId: string, status: 'verified' | 'unverified' | 'pending'): Promise<void> => {
    const { error } = await supabase
        .from('workers')
        .update({ verification_status: status })
        .eq('id', workerId);

    if (error) throw error;
};

export const setWorkerStatus = async (workerId: string, status: string): Promise<void> => {
    console.log("setWorkerStatus called:", { workerId, status });

    // Update 'profiles' status as primary
    const { data: pData, error: pErr } = await supabase.from('profiles').update({ status }).eq('id', workerId);
    console.log("Profile status update result:", { pData, pErr });
    if (pErr) {
        console.error("Profile status update ERROR:", pErr);
        throw pErr;
    }

    // Also update 'workers' status if column exists (it helps with filtering)
    const { data: wData, error: wErr } = await supabase.from('workers').update({ status }).eq('id', workerId);
    console.log("Worker status update result:", { wData, wErr });
    if (wErr) console.warn("Worker status sync warn:", wErr);
};

// Worker Invitation Logic
export const inviteWorker = async (details: { phone: string; email: string; name: string; skill: string }) => {
    // 1. Check if user already exists
    // (Optional, for now just insert to invites, trigger handles the rest)

    // 2. Insert into worker_invites
    const { error } = await supabase.from('worker_invites').insert({
        email: details.email,
        phone: details.phone,
        name: details.name,
        skill: details.skill
    });
    if (error) throw error;
};

export const grantWorkerTrial = async (workerId: string, days: number): Promise<void> => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + days); // Add days

    // ISO string for Postgres timestamptz
    const endStr = endDate.toISOString();

    const { error } = await supabase.from('workers').update({
        subscription_plan: 'trial',
        subscription_end: endStr,
        status: 'active' // Ensure active
    }).eq('id', workerId);

    if (error) {
        console.error("Grant Trial fail", error);
        throw error;
    }
};

export const getWorkerInvites = async () => {
    const { data } = await supabase.from('worker_invites').select('*').order('created_at', { ascending: false });
    return data || [];
};

export const deleteWorker = async (workerId: string): Promise<void> => {
    try {
        // 1. Delete worker record
        const { error: workerError } = await supabase.from('workers').delete().eq('id', workerId);
        if (workerError) {
            console.error("Delete Worker Record Error:", workerError);
            throw workerError;
        }

        // 2. Delete profile (CASCADE should handle this, but explicit is safer)
        const { error: profileError } = await supabase.from('profiles').delete().eq('id', workerId);
        if (profileError) {
            console.error("Delete Worker Profile Error:", profileError);
            // Continue - worker record is deleted
        }

        // 3. Auth user cleanup NOTE:
        // Deleting a Supabase auth user requires service_role key which MUST NOT
        // be in the client bundle. Handle this via Edge Function or DB trigger.
        console.log("Worker and profile records deleted for:", workerId);
        console.warn("⚠️ Auth user not deleted (requires server-side Edge Function). Manual cleanup may be needed.");
    } catch (e) {
        console.error("Critical error in deleteWorker:", e);
        throw e;
    }
};

// ==========================================
// LOCATION UTILITIES
// ==========================================

/** Haversine formula: returns distance in km between two GPS coords */
export const haversineKm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371; // Earth radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ==========================================
// REAL-TIME LISTENERS
// ==========================================

/**
 * Listen to available online workers. 
 * Optional customerLat/customerLng: when provided, computes real GPS distance 
 * and filters to workers within MAX_RADIUS_KM.
 */
export const MAX_WORKER_RADIUS_KM = 20; // ✅ Show workers within 20km only

export const listenToOnlineWorkers = (
    callback: (workers: WorkerProfile[]) => void,
    customerLat?: number,
    customerLng?: number,
    maxRadiusKm: number = 20
) => {
    const fetch = async () => {
        // 1. Get all online, verified, active workers WITH their location
        const { data: workersData } = await supabase.from('workers')
            .select('*, profiles!inner(current_lat, current_lng, last_location_update)')
            .eq('is_online', true)
            .eq('verification_status', 'verified')
            .eq('status', 'active');

        if (!workersData || workersData.length === 0) {
            callback([]);
            return;
        }

        // 2. Get busy worker IDs
        const { data: activeJobs } = await supabase.from('jobs')
            .select('worker_id')
            .in('status', ['ACCEPTED', 'IN_PROGRESS', 'OTP_VERIFIED']);
        const busyWorkerIds = new Set((activeJobs || []).map((j: any) => j.worker_id).filter(Boolean));

        // 3. Filter busy workers and compute real GPS distances
        const hasCustomerLoc = typeof customerLat === 'number' && typeof customerLng === 'number'
            && customerLat !== 0 && customerLng !== 0;

        const available = workersData
            .filter((w: any) => !busyWorkerIds.has(w.id))
            .map((w: any) => {
                // Worker GPS: prefer profiles.current_lat (updated in real-time) over workers.location
                const wLat: number = w.profiles?.current_lat || 0;
                const wLng: number = w.profiles?.current_lng || 0;

                let distanceKm = 0;
                if (hasCustomerLoc && wLat && wLng) {
                    distanceKm = parseFloat(haversineKm(customerLat!, customerLng!, wLat, wLng).toFixed(1));
                }

                return { ...mapWorkerFromDB(w), distanceKm, currentLat: wLat, currentLng: wLng };
            })
            .filter((w: any) => {
                // ✅ If we have customer location, filter to MAX_WORKER_RADIUS_KM
                // If no customer location yet, show all (graceful fallback)
                if (!hasCustomerLoc) return true;
                // Workers with NO GPS coords (0,0) - still show them (can't filter without their coords)
                if (!w.currentLat || !w.currentLng) return true;
                return w.distanceKm <= maxRadiusKm;
            })
            .sort((a: any, b: any) => a.distanceKm - b.distanceKm); // Closest first

        console.log(`📍 Online Workers: ${workersData.length} total, ${available.length} within ${maxRadiusKm}km`);
        callback(available);
    };

    fetch();

    const channel = supabase
        .channel(`public:workers:online:${Date.now()}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'workers' }, () => fetch())
        .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs' }, () => fetch())
        // ✅ Re-compute distances when worker locations change in profiles table
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles' }, (payload) => {
            // Only re-fetch if a location field changed (avoid unnecessary fetches)
            if (payload.new?.current_lat !== undefined || payload.new?.current_lng !== undefined) {
                fetch();
            }
        })
        .subscribe();

    return () => { supabase.removeChannel(channel); };
};



export const listenToCategories = (callback: (categories: ServiceCategory[]) => void) => {
    const fetch = async () => {
        // Initial check: if no categories, insert defaults
        const { count } = await supabase.from('service_categories').select('*', { count: 'exact', head: true });
        if (count === 0) {
            const defaults = DEFAULT_SERVICE_CATEGORIES.map(c => ({
                name: c.name,
                base_price: c.basePrice,
                is_active: true
            }));
            await supabase.from('service_categories').insert(defaults);
        }

        const { data } = await supabase.from('service_categories').select('*').order('name');
        callback((data || []).map(d => ({
            ...d,
            basePrice: d.base_price,
            isActive: d.is_active,
            parentId: d.parent_id
        })));
    };
    fetch();

    // Subscribe
    const channel = supabase.channel('public:categories')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'service_categories' }, fetch)
        .subscribe();

    return () => { supabase.removeChannel(channel); };
};

export const listenToPlatformConfig = (callback: (config: PlatformFeeConfig) => void) => {
    const fetch = async () => {
        const { data, error } = await supabase.from('platform_config').select('*');

        // Default Config
        const merged: any = { ...DEFAULT_FEE_CONFIG };

        if (!error && data) {
            // 1. First apply 'fee_config' blob if it exists (Base layer)
            const feeConfigRow = (data as any[]).find(r => r.key_name === 'fee_config');
            if (feeConfigRow && typeof feeConfigRow.value === 'object') {
                Object.assign(merged, feeConfigRow.value);
            }

            // 2. Then apply individual keys (Override layer)
            const cleanStr = (val: any) => (typeof val === 'string' && val.startsWith('"') && val.endsWith('"') ? val.slice(1, -1) : val);

            (data as any[]).forEach((row) => {
                if (row.key_name === 'base_commission_percent') merged.baseCommissionPercent = Number(row.value);
                if (row.key_name === 'new_worker_free_jobs_count') merged.newWorkerFreeJobsCount = Number(row.value);
                if (row.key_name === 'dynamic_multiplier') merged.dynamicMultiplier = Number(row.value);
                if (row.key_name === 'is_system_free_mode') merged.isSystemFreeMode = String(row.value) === 'true';

                if (row.key_name === 'landing_background_type') merged.landingBackgroundType = cleanStr(row.value);
                if (row.key_name === 'landing_video_url') merged.landingVideoUrl = cleanStr(row.value);
                if (row.key_name === 'landing_image_url') merged.landingImageUrl = cleanStr(row.value);
                if (row.key_name === 'payment_qr_url') merged.paymentQrUrl = cleanStr(row.value);
                if (row.key_name === 'upi_id') merged.upiId = cleanStr(row.value);
                if (row.key_name === 'bank_details') merged.bankDetails = cleanStr(row.value);

                if (row.key_name === 'subscription_price') merged.subscriptionPrice = Number(row.value); // ✅ Fixed missing key
                if (row.key_name === 'trial_duration_days') merged.trialDurationDays = Number(row.value); // ✅ Fixed missing key

                if (row.key_name === 'google_maps_api_key') merged.googleMapsApiKey = cleanStr(row.value);
                if (row.key_name === 'max_worker_radius_km') merged.maxWorkerRadiusKm = Number(row.value);
            });
        }
        callback(merged);
    };
    fetch();

    const channel = supabase.channel('public:platform_config')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'platform_config' }, fetch)
        .subscribe();

    return () => { supabase.removeChannel(channel); };
};

export const saveCategory = async (cat: ServiceCategory) => {
    const dbCat: any = {
        name: cat.name,
        base_price: cat.basePrice,
        is_active: cat.isActive,
        parent_id: cat.parentId || null
    };
    if (cat.id) dbCat.id = cat.id;

    const { error } = await supabase.from('service_categories').upsert(dbCat);
    if (error) throw error;
};



export const postJob = async (job: Partial<JobRequest>): Promise<string> => {
    // Verify Auth matches Customer ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");
    if (user.id !== job.customerId) {
        console.error("Auth Mismatch: Logged in ID", user.id, "vs Request ID", job.customerId);
        throw new Error("Security Error: You can only post jobs for yourself.");
    }

    // Fetch latest customer details for synchronization
    const { data: customerProfile } = await supabase
        .from('profiles')
        .select('phone, first_name, last_name, avatar_url')
        .eq('id', user.id)
        .single();

    if (customerProfile) {
        job.customerName = `${customerProfile.first_name || ''} ${customerProfile.last_name || ''}`.trim() || job.customerName;
        job.customerPhone = customerProfile.phone || job.customerPhone;
        job.customerAvatar = customerProfile.avatar_url || job.customerAvatar;
    }

    // ✅ CRITICAL FIX: Check if worker is online (for direct bookings)
    // This prevents offline workers from receiving job notifications
    if (job.workerId) {
        const { data: workerStatus, error: workerError } = await supabase
            .from('workers')
            .select('is_online, name, verification_status')
            .eq('id', job.workerId)
            .single();

        if (workerError || !workerStatus) {
            throw new Error("Worker not found. Please try again.");
        }

        // Check verification status
        if (workerStatus.verification_status !== 'verified') {
            throw new Error(`${workerStatus.name || 'Worker'} is not verified yet.`);
        }

        // ✅ MAIN FIX: Block job creation if worker is offline
        if (!workerStatus.is_online) {
            console.error("❌ Direct Booking Failed: Worker is OFFLINE", workerStatus.name);
            throw new Error(`${workerStatus.name || 'This worker'} is currently offline. Please try again when they are online.`);
        }

        console.log("✅ Direct Booking Validation Passed:", {
            worker: workerStatus.name,
            isOnline: workerStatus.is_online,
            verificationStatus: workerStatus.verification_status
        });
    }

    const dbJob: any = {
        customer_id: user.id, // Enforce correct ID
        customer_name: job.customerName,
        customer_avatar: job.customerAvatar,
        customer_phone: job.customerPhone,
        skill: job.skill,
        description: job.description,
        status: 'OPEN',
        price: job.price,
        otp: Math.floor(1000 + Math.random() * 9000).toString(),
        completion_otp: Math.floor(1000 + Math.random() * 9000).toString(),
        date: job.date,
        is_broadcast: true,
        // ✅ FIX: Save location details
        work_address: job.workAddress,
        coordinates: job.coordinates
    };

    if (job.workerId) {
        dbJob.worker_id = job.workerId;
        dbJob.worker_name = job.workerName;
        dbJob.worker_avatar = job.workerAvatar;
        dbJob.is_broadcast = false;
        dbJob.status = 'PENDING'; // Direct booking waits for acceptance

        // CRITICAL: Ensure worker_phone is present for real-time listeners
        if (job.workerPhone) {
            dbJob.worker_phone = job.workerPhone;
        } else {
            // Fallback: Fetch phone from worker profile if missing
            const { data: w } = await supabase.from('workers').select('phone').eq('id', job.workerId).single();
            if (w) dbJob.worker_phone = w.phone;
        }
    }

    console.log("Posting Job Payload:", dbJob);

    const { data, error } = await supabase.from('jobs').insert(dbJob).select('id').single();
    if (error) {
        console.error("Supabase Job Insert Error:", error);
        throw error;
    }
    console.log("Job Posted Successfully, ID:", data.id);
    return data.id;
};

const mapJobFromDB = (j: any): JobRequest => ({
    ...j,
    customerId: j.customer_id,
    workerId: j.worker_id,
    customerName: j.customer_name,
    customerPhone: j.customer_phone,
    customerAvatar: j.customer_avatar,
    workerName: j.worker_name,
    workerPhone: j.worker_phone,
    workerAvatar: j.worker_avatar,
    isBroadcast: j.is_broadcast,
    mediaUrls: j.media_urls,
    voiceNoteUrl: j.voice_note_url,
    completionOtp: j.completion_otp,
    createdAt: j.created_at,
    paymentMethod: j.payment_method || 'CASH',
    isPaid: j.is_paid,
    platformFee: j.platform_fee,
    workerEarnings: j.worker_earnings,
    userReview: j.user_review,
    completionRequested: j.completion_requested,
    completionMediaUrls: j.completion_media_urls,
    workAddress: j.work_address,
    coordinates: j.coordinates,
    // ✅ Price Negotiation Fields
    priceSetByWorker: j.price_set_by_worker,
    priceAcceptedByCustomer: j.price_accepted_by_customer,
    cancelledBy: j.cancelled_by,
});

export const listenToMyJobs = (phone: string, userId: string, role: string, callback: (jobs: JobRequest[]) => void) => {
    let currentJobs: JobRequest[] = [];

    const fetch = async () => {
        let query = supabase.from('jobs').select('*');

        if (role === 'CUSTOMER') {
            query = query.or(`customer_id.eq.${userId},customer_phone.eq.${phone}`);
        } else {
            query = query.or(`worker_id.eq.${userId},worker_phone.eq.${phone}`);
        }

        const { data } = await query.order('created_at', { ascending: false });
        currentJobs = (data || []).map(mapJobFromDB);
        callback(currentJobs);
    };
    fetch();

    const channel = supabase.channel(`my_jobs_${userId}_${Date.now()}`)
        .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'jobs',
            filter: role === 'CUSTOMER' ? `customer_id=eq.${userId}` : `worker_id=eq.${userId}`
        }, (payload) => {
            // ✅ INSTANT UPDATE: Apply changes directly from payload without network round-trip
            if (payload.eventType === 'INSERT') {
                // New job - add to list and trigger full fetch for complete data
                const newJob = mapJobFromDB(payload.new);
                currentJobs = [newJob, ...currentJobs];
                callback(currentJobs);
                // Also do a full fetch to ensure we have all fields
                fetch();
            } else if (payload.eventType === 'UPDATE') {
                // ⚡ STATUS CHANGE: Update INSTANTLY from payload (no re-fetch needed)
                const updatedJob = mapJobFromDB(payload.new);
                const idx = currentJobs.findIndex(j => j.id === updatedJob.id);
                if (idx !== -1) {
                    currentJobs = [
                        ...currentJobs.slice(0, idx),
                        { ...currentJobs[idx], ...updatedJob },
                        ...currentJobs.slice(idx + 1)
                    ];
                } else {
                    currentJobs = [updatedJob, ...currentJobs];
                }
                callback(currentJobs);
            } else if (payload.eventType === 'DELETE') {
                currentJobs = currentJobs.filter(j => j.id !== payload.old.id);
                callback(currentJobs);
            }
        })
        .subscribe();
    return () => { supabase.removeChannel(channel); };
};


export const updateJobStatus = async (jobId: string, status: string, extra?: any) => {
    const updates: any = { status };
    if (extra?.workerId) {
        if (extra.workerId) updates.worker_id = extra.workerId;
        if (extra.workerName) updates.worker_name = extra.workerName;
        if (extra.workerPhone) updates.worker_phone = extra.workerPhone;
        if (extra.workerAvatar) updates.worker_avatar = extra.workerAvatar;
    }

    // Handle completion request fields
    if (extra?.completionRequested !== undefined) {
        updates.completion_requested = extra.completionRequested;
    }
    if (extra?.completionMediaUrls) {
        updates.completion_media_urls = extra.completionMediaUrls;
    }

    // Handle Payment Fields
    if (extra?.isPaid !== undefined) updates.is_paid = extra.isPaid;
    if (extra?.paymentMethod) updates.payment_method = extra.paymentMethod;
    if (extra?.platformFee !== undefined) updates.platform_fee = extra.platformFee;
    if (extra?.workerEarnings !== undefined) updates.worker_earnings = extra.workerEarnings;

    // Handle Rating and Price Negotiation
    if (extra?.price !== undefined) updates.price = extra.price;
    if (extra?.priceSetByWorker !== undefined) updates.price_set_by_worker = extra.priceSetByWorker;
    if (extra?.priceAcceptedByCustomer !== undefined) updates.price_accepted_by_customer = extra.priceAcceptedByCustomer;

    // Handle Cancellation
    if (extra?.cancelledBy) updates.cancelled_by = extra.cancelledBy;

    // Handle Review
    if (extra?.userReview) updates.user_review = extra.userReview;

    // ⚡ FAST ACCEPT: Single atomic conditional update (no pre-fetch needed)
    // For ACCEPTED status, add a condition so we only update if the job is still OPEN/PENDING
    // This eliminates the extra SELECT query and race conditions,
    // explicitly bypassing it when we are just updating the negotiated price.
    let query = supabase.from('jobs').update(updates).eq('id', jobId);

    if (status === 'ACCEPTED' && !extra?.priceSetByWorker && !extra?.priceAcceptedByCustomer) {
        query = query.in('status', ['OPEN', 'PENDING']);
    }

    const { data: job, error } = await query.select().single();

    if (error) {
        // For ACCEPTED: If no matching rows, the job was already taken or cancelled
        if (status === 'ACCEPTED' && error.code === 'PGRST116') {
            // Single fallback query to give accurate error message
            const { data: check } = await supabase.from('jobs').select('status').eq('id', jobId).single();
            if (!check) throw new Error("Job not found.");
            if (check.status === 'CANCELLED') throw new Error("This job has been cancelled by the customer.");
            if (check.status === 'ACCEPTED' || check.status === 'IN_PROGRESS') throw new Error("This job has already been taken by another worker.");
            throw new Error("Job is no longer available.");
        }
        console.error("Error updating job:", error);
        return;
    }

    // ✅ Update Worker Stats (Earnings & Job Count) only on COMPLETED & Paid
    if (status === 'COMPLETED' && extra?.isPaid && job?.worker_id) {
        console.log("💰 Updating Worker Earnings (RPC)...", { earnings: extra.workerEarnings, workerId: job.worker_id });

        const { error: rpcError } = await supabase.rpc('increment_worker_stats', {
            p_worker_id: job.worker_id,
            p_amount: Number(extra.workerEarnings) || 0
        });

        if (rpcError) {
            console.error("Failed to update stats via RPC:", rpcError);
        } else {
            console.log("✅ Worker Stats Updated via RPC");
        }
    }

    // ✅ Sync Reviews if a review was added
    if (extra?.userReview && job?.worker_id) {
        console.log("⭐ Recalculating Ratings for Worker:", job.worker_id);
        await recalculateWorkerRating(job.worker_id);
    } else if (extra?.userReview && !job?.worker_id) {
        console.warn("⚠️ Review added but Worker ID missing in job record!");
    }
};

export const listenToBroadcastJobs = (category: string, callback: (jobs: JobRequest[]) => void) => {
    let currentJobs: JobRequest[] = [];
    let ignoredIds = new Set<string>();

    const fetch = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();

            // 1. Fetch Ignores
            if (user) {
                const { data: ignored } = await supabase.from('job_ignores').select('job_id').eq('worker_id', user.id);
                ignoredIds = new Set((ignored || []).map((i: any) => i.job_id));
            }

            // 2. Fetch Jobs
            let query = supabase.from('jobs')
                .select('*')
                .eq('is_broadcast', true)
                .eq('status', 'OPEN');

            if (category) {
                query = query.eq('skill', category);
            }

            const { data } = await query;

            // 3. Filter
            const filtered = (data || []).filter((j: any) => !ignoredIds.has(j.id));
            currentJobs = filtered.map(mapJobFromDB);
            callback(currentJobs);
        } catch (e) {
            console.error("Fetch Error", e);
        }
    };
    fetch();

    // Subscribe - use instant updates from payload
    const channel = supabase.channel(`public:jobs:broadcast:${category}_${Date.now()}`)
        .on('postgres_changes', {
            event: 'INSERT',
            schema: 'public',
            table: 'jobs',
            filter: category ? `skill=eq.${category}` : undefined
        }, (payload) => {
            if (payload.new?.is_broadcast && payload.new?.status === 'OPEN' && !ignoredIds.has(payload.new.id)) {
                // ⚡ Instantly add new broadcast job
                const newJob = mapJobFromDB(payload.new);
                currentJobs = [newJob, ...currentJobs];
                callback(currentJobs);
            }
        })
        .on('postgres_changes', {
            event: 'UPDATE',
            schema: 'public',
            table: 'jobs',
            filter: category ? `skill=eq.${category}` : undefined
        }, (payload) => {
            if (payload.new?.status !== 'OPEN') {
                // ⚡ Job was accepted/cancelled - remove instantly from broadcast list
                currentJobs = currentJobs.filter(j => j.id !== payload.new.id);
                callback(currentJobs);
            }
        })
        .on('postgres_changes', {
            event: 'DELETE',
            schema: 'public',
            table: 'jobs'
        }, (payload) => {
            currentJobs = currentJobs.filter(j => j.id !== payload.old.id);
            callback(currentJobs);
        })
        .subscribe((status, err) => {
            if (status === 'SUBSCRIBED') {
                console.log(`Broadcast Subscription Active (${category})`);
            } else if (status === 'CHANNEL_ERROR') {
                console.warn(`Broadcast Subscription (${category}) experienced a minor connection hiccup, will retry automatically.`, err);
            } else {
                console.log(`Broadcast Subscription (${category}):`, status);
            }
        });
    return () => { supabase.removeChannel(channel); };
};

export const ignoreJob = async (jobId: string, workerId: string) => {
    await supabase.from('job_ignores').insert({ job_id: jobId, worker_id: workerId });
};

// Update listenToBroadcastJobs implementation above (re-writing fetch logic)
// I need to use replace_file_content on the whole function because 'fetch' is inside.
// Wait, I can only replace a chunk.
// I will rewrite listenToBroadcastJobs entirely.

export const submitReport = async (report: Partial<Report>) => {
    const dbReport = {
        sender_id: report.reporterId,
        target_id: report.reportedId,
        type: report.reportedRole,
        reason: report.reason,
        description: report.details,
        job_id: report.jobId,
        status: 'PENDING',
        created_at: new Date().toISOString()
    };

    const { error } = await supabase.from('reports').insert(dbReport);
    if (error) {
        console.error('Report submission error:', error);
        throw error;
    }
};

export const listenToReports = (callback: (reports: Report[]) => void) => {
    const fetch = async () => {
        const { data, error } = await supabase.from('reports')
            .select(`
                *,
                sender:sender_id(first_name, last_name)
            `)
            .order('created_at', { ascending: false });

        if (error) console.error("Error fetching reports:", error);

        const mapped: Report[] = (data || []).map((r: any) => ({
            id: r.id,
            reason: r.reason,
            details: r.description,
            timestamp: r.created_at,
            reporterId: r.sender_id, // Added missing field
            reporterName: r.sender ? `${r.sender.first_name} ${r.sender.last_name}`.trim() : 'Unknown',
            reportedId: r.target_id,
            reportedName: 'Target User',
            reportedRole: r.type as UserRole,
            status: r.status,
            jobId: r.target_id
        }));
        callback(mapped);
    };
    fetch();

    const channel = supabase.channel('realtime_reports')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'reports' }, fetch)
        .subscribe();
    return () => { supabase.removeChannel(channel); };
};

export const updateReportStatus = async (id: string, status: string) => {
    const { error } = await supabase.from('reports').update({ status }).eq('id', id);
    if (error) throw error;
};

export const listenToWithdrawals = (callback: (requests: WithdrawalRequest[]) => void) => {
    const fetch = async () => {
        const { data } = await supabase.from('withdrawals').select('*');
        callback((data || []).map(d => ({ ...d, requestedAt: d.created_at } as WithdrawalRequest)));
    };
    fetch();
    return () => { };
};

export const updateWithdrawalStatus = async (id: string, status: string) => {
    await supabase.from('withdrawals').update({ status }).eq('id', id);
};

export const getWorkerReviews = async (workerId: string): Promise<Review[]> => {
    // Correctly query the 'jobs' table for user_reviews
    const { data } = await supabase.from('jobs')
        .select('*')
        .eq('worker_id', workerId)
        .not('user_review', 'is', null)
        .order('created_at', { ascending: false });

    return (data || []).map((j: any) => ({
        id: j.id,
        author: j.customer_name || 'Customer',
        rating: j.user_review?.rating || 5,
        text: j.user_review?.text || '',
        date: new Date(j.created_at).toLocaleDateString(),
        mediaUrls: j.user_review?.mediaUrls,
        voiceNoteUrl: j.user_review?.voiceNoteUrl
    }));
};

const recalculateWorkerRating = async (workerId: string) => {
    // 1. Fetch all reviews
    const reviews = await getWorkerReviews(workerId);

    if (reviews.length === 0) return;

    // 2. Calculate Stats
    const total = reviews.reduce((sum, r) => sum + r.rating, 0);
    const avg = Number((total / reviews.length).toFixed(1));

    // 3. Update Worker Table
    await supabase.from('workers').update({
        rating: avg,
        review_count: reviews.length
    }).eq('id', workerId);

    console.log(`⭐ Ratings Sync: Updated Worker ${workerId} to ${avg} (${reviews.length} reviews)`);
};

export const listenToAds = (callback: (ads: AdBanner[]) => void) => {
    const fetch = async () => {
        const { data } = await supabase.from('ad_banners').select('*').order('created_at', { ascending: false });

        const mapped: AdBanner[] = (data || []).map((ad: any) => ({
            id: ad.id,
            imageUrl: ad.image_url,
            linkUrl: ad.link_url,
            title: ad.title,
            position: ad.position,
            isActive: ad.is_active,
            isUniversal: ad.is_universal,
            city: ad.city
        }));
        callback(mapped);
    };
    fetch();

    const channel = supabase.channel('realtime_ads')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'ad_banners' }, fetch)
        .subscribe();
    return () => { supabase.removeChannel(channel); };
};

export const saveAd = async (ad: AdBanner): Promise<void> => {
    const dbAd = {
        id: ad.id,
        image_url: ad.imageUrl,
        link_url: ad.linkUrl,
        title: ad.title,
        position: ad.position,
        is_active: ad.isActive,
        is_universal: ad.isUniversal,
        city: ad.city
    };
    const { error } = await supabase.from('ad_banners').upsert(dbAd);
    if (error) throw error;
};

export const deleteAd = async (id: string): Promise<void> => {
    const { error } = await supabase.from('ad_banners').delete().eq('id', id);
    if (error) throw error;
};

// --- Platform Config ---
// Uses imported DEFAULT_FEE_CONFIG

export const getPlatformConfig = async (): Promise<PlatformFeeConfig> => {
    const { data } = await supabase.from('platform_config').select('value').eq('key_name', 'fee_config').single();
    if (data?.value) return { ...DEFAULT_FEE_CONFIG, ...data.value };
    return DEFAULT_FEE_CONFIG;
};

export const savePlatformConfig = async (config: PlatformFeeConfig) => {
    console.log("💾 Saving Platform Config:", config);

    // Save as complete object for backwards compatibility
    await supabase.from('platform_config').upsert(
        { key_name: 'fee_config', value: config },
        { onConflict: 'key_name' }
    );

    // ✅ NEW: Save each field individually (matches how listener reads them)
    const fieldMappings = [
        { key: 'base_commission_percent', value: config.baseCommissionPercent },
        { key: 'new_worker_free_jobs_count', value: config.newWorkerFreeJobsCount || 0 },
        { key: 'dynamic_multiplier', value: config.dynamicMultiplier || 1.0 },
        { key: 'is_system_free_mode', value: config.isSystemFreeMode ? 'true' : 'false' },
        { key: 'landing_background_type', value: config.landingBackgroundType || 'image' },
        { key: 'landing_video_url', value: config.landingVideoUrl || '' },
        { key: 'landing_image_url', value: config.landingImageUrl || '' },
        { key: 'payment_qr_url', value: config.paymentQrUrl || '' },
        { key: 'upi_id', value: config.upiId || '' },
        { key: 'bank_details', value: config.bankDetails || '' },
        { key: 'subscription_price', value: config.subscriptionPrice || 0 },
        { key: 'trial_duration_days', value: config.trialDurationDays || 7 },
        { key: 'google_maps_api_key', value: config.googleMapsApiKey || '' },
        { key: 'max_worker_radius_km', value: config.maxWorkerRadiusKm || 10 }
    ];

    // Batch upsert all fields
    const promises = fieldMappings.map(({ key, value }) =>
        supabase.from('platform_config').upsert(
            { key_name: key, value: value },
            { onConflict: 'key_name' }
        )
    );

    const results = await Promise.all(promises);

    // Check for errors
    const errors = results.filter(r => r.error);
    if (errors.length > 0) {
        console.error("❌ Platform config save errors:", errors);
        throw errors[0].error;
    }

    console.log("✅ Platform config saved successfully! All individual keys written.");
};



// --- API Keys ---
export const getApiKeys = async () => {
    const { data } = await supabase.from('platform_config').select('value').eq('key_name', 'api_keys').single();
    return data?.value || { googleMaps: '' };
};

export const saveApiKeys = async (keys: any) => {
    const { error } = await supabase.from('platform_config').upsert({ key_name: 'api_keys', value: keys }, { onConflict: 'key_name' });
    if (error) throw error;
};

// --- Firebase Config (Legacy/Unused but stubbed to prevent crash) ---


export const resetUserPassword = async (identifier: string) => {
    if (identifier.includes('@')) {
        const { error } = await supabase.auth.resetPasswordForEmail(identifier, {
            redirectTo: window.location.origin + '/reset-password', // Ensure this route exists or handles tokens
        });
        if (error) throw error;
        return { success: true };
    } else {
        // Phone reset typically requires OTP flow, not supported by simple link
        throw new Error("Password reset currently only supported for Email. Please contact support.");
    }
};

export const getWorkerTransactions = async (workerId: string): Promise<Transaction[]> => {
    const { data } = await supabase.from('transactions').select('*').eq('user_id', workerId);
    return (data || []) as Transaction[];
};

export const requestWithdrawal = async (data: Partial<WithdrawalRequest>): Promise<void> => {
    // Need worker_id (UUID)
    // await supabase.from('withdrawals').insert(data);
};

export const verifyAdminCredentials = async (email: string, pass: string) => {
    // 1. Authenticate with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email,
        password: pass
    });

    if (authError) {
        console.error("Admin Auth Error:", authError);
        return { success: false, error: authError.message };
    }

    if (!authData.user) return { success: false, error: 'Authentication failed.' };

    // 2. Verify Admin Role in Profile
    const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authData.user.id)
        .eq('is_admin', true)
        .single();

    if (profileError || !profileData) {
        console.error("Admin Profile Error:", profileError);
        await supabase.auth.signOut(); // Force logout if not admin
        return { success: false, error: 'Access denied: Not an Administrator.' };
    }

    return { success: true, user: mapProfileFromDB(profileData) };
};


// (Removed duplicate logic)

export const getCMSContent = async () => {
    const { data, error } = await supabase.from('cms_content').select('*');

    const defaultPages = [
        { slug: 'privacy', title: 'Privacy Policy', content: '# Privacy Policy\n\nEdit this content...' },
        { slug: 'terms', title: 'Terms of Service', content: '# Terms of Service\n\nEdit this content...' },
        { slug: 'help', title: 'Help & Support', content: '# Help Center\n\nHow can we help?' }
    ];

    if (error || !data) return defaultPages;

    const dbPages = data.map((row: any) => ({
        slug: row.slug,
        title: row.title,
        content: row.content
    }));

    // Merge defaults
    return defaultPages.map(def => {
        const found = dbPages.find(p => p.slug === def.slug);
        return found || def;
    });
};

export const saveCMSContent = async (slug: string, title: string, content: string) => {
    const { error } = await supabase.from('cms_content').upsert({
        slug,
        title,
        content
    });
    if (error) throw error;
};

export const getFirebaseConfig = async () => {
    const { data } = await supabase.from('platform_config').select('value').eq('key_name', 'firebase_config').single();
    return data ? data.value : {};
};

export const saveFirebaseConfig = async (config: any) => {
    const { error } = await supabase.from('platform_config').upsert({ key_name: 'firebase_config', value: config }, { onConflict: 'key_name' });
    if (error) throw error;
};

export const updateWorkerLocation = async (workerId: string, lat: number, lng: number) => {
    const location = `POINT(${lng} ${lat})`;
    const { error } = await supabase.from('workers').update({ location: location }).eq('id', workerId);
    if (error) console.error("Location Update Failed", error);
};

export const listenToWorkerLocation = (workerId: string, callback: (coords: { lat: number, lng: number }) => void) => {
    console.log("🔔 Subscribing to worker location updates:", workerId);

    // 1. Initial Fetch
    supabase.from('workers').select('current_lat, current_lng, location').eq('id', workerId).maybeSingle()
        .then(({ data: newRow }) => {
            if (newRow) {
                if (newRow.current_lat && newRow.current_lng) {
                    callback({ lat: parseFloat(newRow.current_lat), lng: parseFloat(newRow.current_lng) });
                } else if (newRow.location) {
                    try {
                        const matches = newRow.location.match(/POINT\(([^ ]+) ([^ ]+)\)/);
                        if (matches) callback({ lng: parseFloat(matches[1]), lat: parseFloat(matches[2]) });
                    } catch (e) { }
                }
            }
        });

    // 2. Realtime Listener
    const channel = supabase.channel(`tracking:${workerId}`)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'workers', filter: `id=eq.${workerId}` }, (payload) => {
            const newRow = payload.new;
            console.log("📍 Worker location update received:", newRow);

            // ✅ FIX: Use current_lat and current_lng columns
            if (newRow && newRow.current_lat && newRow.current_lng) {
                const coords = {
                    lat: parseFloat(newRow.current_lat),
                    lng: parseFloat(newRow.current_lng)
                };
                console.log("✅ Location parsed:", coords);
                callback(coords);
            } else if (newRow && newRow.location) {
                // Fallback: Try to parse location column (PostGIS format)
                try {
                    const matches = newRow.location.match(/POINT\(([^ ]+) ([^ ]+)\)/);
                    if (matches) {
                        const coords = { lng: parseFloat(matches[1]), lat: parseFloat(matches[2]) };
                        console.log("✅ Location parsed from PostGIS:", coords);
                        callback(coords);
                    }
                } catch (e) {
                    console.error("❌ Geo Parse Error", e);
                }
            }
        })
        .subscribe();
    return () => {
        console.log("🔕 Unsubscribing from worker location:", workerId);
        supabase.removeChannel(channel);
    };
};

export const listenToCustomerLocation = (customerId: string, callback: (coords: { lat: number, lng: number }) => void) => {
    console.log("🔔 Subscribing to customer location updates:", customerId);

    // 1. Initial Fetch
    supabase.from('profiles').select('current_lat, current_lng').eq('id', customerId).maybeSingle()
        .then(({ data: newRow }) => {
            if (newRow && newRow.current_lat && newRow.current_lng) {
                callback({ lat: parseFloat(newRow.current_lat), lng: parseFloat(newRow.current_lng) });
            }
        });

    // 2. Realtime Listener
    const channel = supabase.channel(`tracking_customer:${customerId}`)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${customerId}` }, (payload) => {
            const newRow = payload.new;
            // console.log("📍 Customer location update received:", newRow);

            if (newRow && newRow.current_lat && newRow.current_lng) {
                const coords = {
                    lat: parseFloat(newRow.current_lat),
                    lng: parseFloat(newRow.current_lng)
                };
                // console.log("✅ Customer Location parsed:", coords);
                callback(coords);
            }
        })
        .subscribe();
    return () => {
        console.log("🔕 Unsubscribing from customer location:", customerId);
        supabase.removeChannel(channel);
    };
};

// ==========================================
// REAL-TIME WALLET & DASHBOARD
// ==========================================

export const listenToWalletUpdates = (userId: string, callback: (balance: number, transactions: any[]) => void) => {
    const fetch = async () => {
        try {
            // 1. Get Balance - use maybeSingle to handle RLS gracefully
            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('wallet_balance')
                .eq('id', userId)
                .maybeSingle();

            if (profileError) {
                console.warn('Wallet balance fetch error:', profileError);
            }

            // 2. Get Transactions
            const { data: txs, error: txError } = await supabase.from('transactions')
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });

            if (txError) {
                console.warn('Transactions fetch error:', txError);
            }

            callback(profile?.wallet_balance || 0, (txs || []).map((t: any) => ({
                id: t.id,
                userId: t.user_id,
                type: t.type,
                amount: t.amount,
                description: t.description,
                date: t.created_at,
                status: t.status
            })));
        } catch (e) {
            console.error('Unexpected error in wallet listener:', e);
            callback(0, []); // Return safe defaults
        }
    };
    fetch();

    const channel = supabase.channel(`wallet_${userId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'transactions', filter: `user_id=eq.${userId}` }, fetch)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${userId}` }, fetch)
        .subscribe();

    return () => { supabase.removeChannel(channel); };
};

export const listenToAdminDashboard = (callback: (stats: any) => void) => {
    const fetch = async () => {
        const { count: users } = await supabase.from('profiles').select('*', { count: 'exact', head: true });
        const { count: workers } = await supabase.from('workers').select('*', { count: 'exact', head: true });
        const { count: jobs } = await supabase.from('jobs').select('*', { count: 'exact', head: true }).eq('status', 'COMPLETED');

        // Calculate Revenue (clientside sum for demo, real prod should use RPC)
        const { data: income } = await supabase.from('jobs').select('price').eq('status', 'COMPLETED').eq('is_paid', true);
        const totalRevenue = (income || []).reduce((acc: any, curr: any) => acc + (Number(curr.price) * 0.1), 0); // Mock 10% commission

        callback({
            totalUsers: users || 0,
            activeWorkers: workers || 0,
            jobsCompleted: jobs || 0,
            totalRevenue
        });
    };
    fetch();

    // Subscribe to all major tables (Heavy but requested "Real Time")
    const channel = supabase.channel('admin_dashboard')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, fetch)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'workers' }, fetch)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs' }, fetch)
        .subscribe();

    return () => { supabase.removeChannel(channel); };
};

// --- CHAT FUNCTIONS ---

export const sendMessage = async (jobId: string, senderId: string, text: string) => {
    const { error } = await supabase.from('messages').insert({
        job_id: jobId,
        sender_id: senderId,
        text: text
    });
    if (error) {
        console.error("Error sending message:", error);
        throw error;
    }
};

export const listenToMessages = (jobId: string, callback: (messages: ChatMessage[]) => void) => {
    const fetch = async () => {
        console.log(`💬 Fetching chat history for jobId: ${jobId}`);
        const { data, error } = await supabase
            .from('messages')
            .select('*')
            .eq('job_id', jobId)
            .order('created_at', { ascending: true });

        if (error) {
            console.error("❌ Error fetching messages:", error);
            return;
        }

        console.log(`✅ Loaded ${data?.length || 0} messages for jobId: ${jobId}`);

        const mapped: ChatMessage[] = (data || []).map(m => ({
            id: m.id,
            senderId: m.sender_id,
            text: m.text,
            timestamp: new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }));
        callback(mapped);
    };

    fetch();

    const channel = supabase.channel(`chat_${jobId}`)
        .on('postgres_changes', {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `job_id=eq.${jobId}`
        }, fetch)
        .subscribe();

    return () => { supabase.removeChannel(channel); };
};
